/**
 * AB实验页面「事件操作」Hook
 *
 * 封装内容：
 * - 表单状态（formState）、默认日期范围、updateFormState、用户类型切换
 * - 计算、清空、保存、编辑加载及表格/详情状态
 * - 透出 useAbConfig 的配置数据供页面筛选器使用
 *
 * @param params - 入参，见 IUseAbExperimentActionsParams
 * @returns 表单状态、更新方法、事件回调和配置数据，见 IUseAbExperimentActionsResult
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceFn } from 'ahooks';
import { history, shallowEqual, useSelector } from 'umi';
import moment, { Moment } from 'moment';
import { Modal, notification } from 'tea-component';

import { calculateAbResult, previewAbSql } from '../service';
import { openCommonDataLabDownloadPage } from '@/pages/datalab/pages/Multidimensional/Analysis/components/AnalysisLineChart';
import { buildCalculateAbResultParams } from '../utils';
import { getChartDetail, saveChart } from '@dbl/services/chart';
import { ChartType } from '@dbl/core/chart';
import {
  IUseAbExperimentActionsParams,
  IUseAbExperimentActionsResult,
  IAbTaskDetail,
  ISaveTaskParams,
} from './types';
import { IABExperimentFormState } from '../types';
import { IApiTableResponse } from '../utils/tableDataConverter';
import { useAbConfig } from './useAbConfig';
import type { TUserType } from '../components/UserType/types';
import { CUSTOM_VALUE } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/components/EventFilter/components/EventFilterContent/config';
import { getLanguage } from '@home/utils/common';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { showPreviewSqlModal } from './utils';
import { LANGUAGE_CN, LANGUAGE_EN } from '@home/_config/config';

export function useAbExperimentActions(
  params: IUseAbExperimentActionsParams
): IUseAbExperimentActionsResult {
  const { gameCode, isEdit, taskId, formatMessage, isEn, pageName } = params;
  const currentGame = useSelector((state: any) => state?.datalab?.currentGame, shallowEqual);

  // ---------- 默认日期与表单状态 ----------
  // 默认过去 7 天（从 7 天前到昨天），用于初始 formState 与清空时重置
  const defaultDateRange = useMemo<[Moment, Moment]>(() => {
    const yesterday = moment().subtract(1, 'day');
    const sevenDaysAgo = moment().subtract(7, 'day');
    return [sevenDaysAgo, yesterday];
  }, []);

  // AB实验表单状态（由 hook 内部维护）
  const [formState, setFormState] = useState<IABExperimentFormState>(() => ({
    userType: 'new_user',
    dateRange: defaultDateRange,
    globalFilterValue: [],
    globalFilterLogic: 'and',
    eventFilterValue: [],
    eventFilterLogic: 'and',
    experimentFilterValue: [],
    metricFilterValue: [],
    dimensionFilterValue: [],
    observationDays: 1,
    limit: 10000,
    chartConfig: {
      sorts: [],
      tableColumnWidthsMap: undefined,
      isTranspose: false,
      selectedMetrics: [],
    },
  }));

  const {
    userType,
    globalFilterValue,
    eventFilterValue,
    metricFilterValue,
  } = formState;

  // ---------- 配置数据 ----------
  // 依赖 formState.userType，用于按用户类型过滤指标/维度等选项
  const {
    allFilters,
    optionFilters,
    eventList,
    allMetrics,
    optionMetrics,
    optionCustomMetrics,
    allDimensions,
    optionDimensions,
    experimentFilters,
    loading,
    rawData,
  } = useAbConfig({
    gameCode,
    userType,
    enabled: true,
  });

  // ---------- 表格与计算状态 ----------
  const [tableData, setTableData] = useState<IApiTableResponse | undefined>(
    undefined
  );
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [hasCalculated, setHasCalculated] = useState<boolean>(false);
  // 上次计算参数 JSON，用于与当前 formState 比较以控制「重新计算」提示
  const [lastCalculatedParamsJson, setLastCalculatedParamsJson] = useState<string | null>(null);
  const [showSaveModel, setShowSaveModel] = useState<boolean>(false);
  const [detailData, setDetailData] = useState<IAbTaskDetail>({});
  const [skipNextLeavePrompt, setSkipNextLeavePrompt] = useState<boolean>(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState<boolean>(false);
  const [downloadRowLimitValue, setDownloadRowLimitValue] = useState<number>(2000);
  /** 预览 SQL 请求中，用于禁用预览按钮 */
  const [sqlPreviewLoading, setSqlPreviewLoading] = useState<boolean>(false);

  // ---------- 表单更新与用户类型切换 ----------
  const updateFormState = useCallback(
    <K extends keyof IABExperimentFormState>(
      key: K,
      value: IABExperimentFormState[K]
    ) => {
      setFormState((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  // 切换用户类型时需过滤掉不适用于该类型的指标，此处预先算出有效 table_id 集合
  const getValidMetricTableIdsForUserType = useCallback(
    (targetUserType: TUserType): Set<string> => {
      const metrics = rawData?.metrics ?? [];
      const nonCustom = metrics.filter((m: any) => !m.is_custom);
      const valid = nonCustom.filter(
        (m: any) =>
          !targetUserType ||
          m.applicable_user_types?.includes('all') ||
          m.applicable_user_types?.includes(targetUserType)
      );
      return new Set(valid.map((m: any) => String(m.mapping_id)));
    },
    [rawData]
  );

  // 获取注册时长名称文本
  const getHoursText = (data: { hoursSinceRegister: string; hoursSinceRegisterCustom: string; }) => {
    const { hoursSinceRegister, hoursSinceRegisterCustom } = data || {};
    const hours = hoursSinceRegister === CUSTOM_VALUE ? hoursSinceRegisterCustom : hoursSinceRegister;
    const textEn = `Hours Since Register(h)${hours}h;`;
    const textCn = `注册时长(h)${hours}h;`;
    return {
      textEn,
      textCn,
    };
  };

  // 用户类型变更时更新 userType，并剔除当前不适用指标，避免选中无效指标
  const handleUserTypeChange = useCallback(
    (value: TUserType) => {
      const isNewUserType = value === 'new_user';
      const validMetricTableIds = getValidMetricTableIdsForUserType(value);
      const filtered = metricFilterValue.length === 0 ? metricFilterValue : metricFilterValue.filter((item: any) => item.is_custom === true || validMetricTableIds.has(item.table_id));
      const needUpdateMetric = filtered.length !== metricFilterValue.length || filtered.some((f: any, i: number) => f.id !== metricFilterValue[i]?.id);
      const newEventFilterValue = eventFilterValue?.map((item: any) => {
        const { eventName, displayNameEn, displayNameCn, isRenamed } = item || {};
        if (!eventName) return item;
        const { textEn: replaceTextEn, textCn: replaceTextCn } = getHoursText(item);
        const { textEn: addTextEn, textCn: addTextCn } = getHoursText({ hoursSinceRegister: '24', hoursSinceRegisterCustom: '' });
        const addDisplayNameEn = isRenamed ? displayNameEn : addTextEn + displayNameEn;
        const addDisplayNameCn = isRenamed ? displayNameCn : addTextCn + displayNameCn;
        const newDisplayNameEn = isNewUserType ? addDisplayNameEn : displayNameEn?.replace(replaceTextEn, "");
        const newDisplayNameCn = isNewUserType ? addDisplayNameCn : displayNameCn?.replace(replaceTextCn, "");
        return {
          ...item,
          displayNameEn: newDisplayNameEn,
          displayNameCn: newDisplayNameCn,
          hoursSinceRegister: isNewUserType ? '24' : '',
          hoursSinceRegisterCustom: '',
        };
      });
      setFormState((prev) => ({
        ...prev,
        userType: value,
        eventFilterValue: newEventFilterValue,
        ...(needUpdateMetric ? { metricFilterValue: filtered } : {}),
      }));
    },
    [metricFilterValue, eventFilterValue, getValidMetricTableIdsForUserType]
  );

  // ---------- 派生状态：计算按钮与「参数未变更」提示 ----------
  // 当前表单参数与上次计算参数一致则为 true，用于控制「重新计算」提示显隐
  const hashEqual = useMemo(() => {
    if (!hasCalculated || lastCalculatedParamsJson === null) {
      return true;
    }
    const currentParamsJson = JSON.stringify(
      buildCalculateAbResultParams(gameCode, formState)
    );
    return currentParamsJson === lastCalculatedParamsJson;
  }, [hasCalculated, gameCode, formState, lastCalculatedParamsJson]);

  // 指标未选、配置加载中或存在未配置的自定义指标时禁用计算按钮
  const disableCalculateButton = useMemo(() => {
    if (!metricFilterValue?.length || loading) {
      return true;
    }
    const hasUnconfiguredCustomMetric = (metricFilterValue ?? []).some(
      (item: any) => item?.is_custom && !item?.custom_metric_config
    );
    return hasUnconfiguredCustomMetric;
  }, [metricFilterValue, loading]);

  // 最大宽度样式
  const isMaxWidth = useMemo(() => {
    const haveDateFilter = globalFilterValue?.some((item) => item?.column_type === 'date');
    return isEn && globalFilterValue?.length > 1 && haveDateFilter;
  }, [isEn, globalFilterValue]);

  // 是否超过限制(每次请求完判断，不依赖limit，否则每次limit变化都会重新计算)
  const isOverLimit = useMemo(() => {
    if (tableLoading || !tableData) {
      return false;
    }
    return tableData?.table_data?.length >= formState?.limit;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableLoading, tableData]);

  // 当前表单与保存内容一致则为 true，用于离开页保存提示（与 Exploration 的 hashEqualForSave 一致）
  const hashEqualForSave = useMemo(() => {
    if (!isEdit) {
      return false;
    }
    if (!detailData?.filter) {
      return true;
    }
    try {
      const filter = typeof detailData.filter === 'string' ? JSON.parse(detailData.filter) : detailData.filter;
      const savedFormState = filter?.formState;
      if (!savedFormState) {
        return false;
      }
      const currentNormalized = {
        ...formState,
        dateRange: [
          formState?.dateRange?.[0]?.format?.('YYYY-MM-DD'),
          formState?.dateRange?.[1]?.format?.('YYYY-MM-DD'),
        ],
      };
      const savedNormalized = {
        ...savedFormState,
        dateRange: Array.isArray(savedFormState.dateRange) ? savedFormState.dateRange : [],
      };
      return JSON.stringify(currentNormalized) === JSON.stringify(savedNormalized);
    } catch {
      return false;
    }
  }, [isEdit, detailData?.filter, formState]);

  // ---------- 事件回调：计算、清空、保存 ----------
  const onCalculate = useCallback(
    (state?: IABExperimentFormState) => {
      const stateUsed = state ?? formState;
      const requestParams = buildCalculateAbResultParams(gameCode, stateUsed);
      const calculateExtInfo = isEdit
        ? JSON.stringify({
          gameName: currentGame?.game_name,
          gameNameCn: currentGame?.game_cn_name,
          pageName,
          pagePath: '/Analysis/ABTest',
          id: taskId ?? '',
          requestParams,
        })
        : JSON.stringify({
          gameName: currentGame?.game_name,
          gameNameCn: currentGame?.game_cn_name,
          pageName: 'A/B Test Analysis',
          pageNameCn: 'A/B实验分析',
          pagePath: '/Analysis/ABTest',
          requestParams,
        });
      buriedBtnPoint({
        buttonId: '602401001',
        extInfo: calculateExtInfo,
        pageId: '602401',
        typeId: 'datalab_pc_v2',
        buttonName: formatMessage({ id: 'dbl_ab_experiment_calculate' }),
        uidType: 'game_code',
        uid: gameCode,
        gameName: currentGame?.game_name,
      });
      setLastCalculatedParamsJson(JSON.stringify(requestParams));
      setTableLoading(true);
      calculateAbResult(requestParams)
        .then((result: any) => {
          setTableData(result as IApiTableResponse);
          // 计算后自动选中第一个指标
          const firstMetricValue = result?.table_data?.[0]?.metric_name?.value;
          const hasSelectedMetrics = Boolean(
            stateUsed?.chartConfig?.selectedMetrics?.length
          );
          // 编辑态加载详情后，避免把已保存的 selectedMetrics 再覆盖成第一个指标
          if (firstMetricValue && !hasSelectedMetrics) {
            updateFormState('chartConfig', {
              ...(stateUsed?.chartConfig || {}),
              selectedMetrics: [firstMetricValue],
            });
          }
        })
        .catch((error: any) => {
          console.warn('计算失败:', error);
          setTableData(undefined);
          notification.error({
            description: formatMessage(
              { id: 'dbl_ab_experiment_calculate_error' },
              { code: String(error?.code ?? error?.message ?? -1) }
            ),
          });
        })
        .finally(() => {
          setTableLoading(false);
          setHasCalculated(true);
        });
    },
    [gameCode, formState, formatMessage, isEdit, taskId, pageName, currentGame]
  );

  // 预览 SQL：调用 previewAbSql 接口，用 state 控制 loading，成功后弹窗展示（参考多维 Search 的 previewSQL）
  const onPreviewSQL = useCallback((): void => {
    setSqlPreviewLoading(true);
    const requestParams = buildCalculateAbResultParams(gameCode, formState);
    previewAbSql(requestParams)
      .then((res: any) => {
        const sql = res?.final_sql ?? res?.sql ?? '';
        showPreviewSqlModal({ sql, formatMessage });
      })
      .catch((error: any) => {
        notification.error({
          description: formatMessage(
            { id: 'dbl_ab_experiment_preview_sql_fail' },
            { code: String(error?.code ?? error?.message ?? -1) }
          ),
        });
      })
      .finally(() => {
        setSqlPreviewLoading(false);
      });
  }, [gameCode, formState, formatMessage]);

  // 下载表格：先打开下载设置弹窗（与多维分析表格下载一致）
  const onDownload = useCallback((): void => {
    setDownloadModalVisible(true);
  }, [pageName]);

  const { run: onDownloadConfirm } = useDebounceFn(
    () => {
      const requestParams = buildCalculateAbResultParams(gameCode, formState);
      const params = {
        ...requestParams,
        language: isEn ? LANGUAGE_EN : LANGUAGE_CN,
        row_limit: downloadRowLimitValue,
        file_name: pageName,
        sorts: formState?.chartConfig?.sorts ?? [],
        transpose: formState?.chartConfig?.isTranspose ?? false,
      };
      openCommonDataLabDownloadPage(
        params,
        '/api/v1/datalab/ab_testing/download_excel'
      ).finally(() => {
        setDownloadModalVisible(false);
      });
    },
    { wait: 500 }
  );

  const onDownloadModalClose = useCallback((): void => {
    setDownloadModalVisible(false);
  }, []);

  // 确认后重置表单与表格状态为默认值
  const onClearAll = useCallback((): void => {
    Promise.resolve(
      Modal.confirm({
        className: 'model-delect',
        message: formatMessage({ id: 'dbl_exploration_clear_message' }),
        description: formatMessage({ id: 'dbl_exploration_clear_des' }),
        okText: formatMessage({ id: 'dbl_common_confirm' }),
        cancelText: formatMessage({ id: 'dbl_common_cancel' }),
      })
    ).then((result: boolean) => {
      if (result) {
        setFormState({
          userType: 'new_user',
          dateRange: defaultDateRange,
          globalFilterValue: [],
          globalFilterLogic: 'and',
          eventFilterValue: [],
          eventFilterLogic: 'and',
          experimentFilterValue: [],
          metricFilterValue: [],
          dimensionFilterValue: [],
          observationDays: 1,
          limit: 10000,
          chartConfig: {
            sorts: [],
            tableColumnWidthsMap: undefined,
            isTranspose: false,
            selectedMetrics: [],
          },
        });
        setTableData(undefined);
        setHasCalculated(false);
        setLastCalculatedParamsJson(null);
      }
    });
  }, [formatMessage, setFormState, defaultDateRange]);

  // 组装 filter（含 formState）并调用保存接口，成功后跳转我的分析
  const saveHandle = useCallback(
    (oParams: ISaveTaskParams) => {
      const filter = {
        formState: {
          ...formState,
          dateRange: [
            formState?.dateRange?.[0]?.format('YYYY-MM-DD'),
            formState?.dateRange?.[1]?.format('YYYY-MM-DD'),
          ],
        },
      };
      const requestParams = {
        ...(oParams || {}),
        old_id: oParams?.oldId,
        task_id: oParams?.oldId ? undefined : (oParams?.taskId || taskId),
        name: oParams?.name ?? '',
        name_cn: oParams?.nameCn ?? '',
        label: oParams?.label ?? '',
        chart_type: ChartType.Table,
        game_code: gameCode,
        type: 2,
        create_time: moment().format('YYYY-MM-DD HH:mm:ss'),
        filter: JSON.stringify(filter),
      };
      saveChart(requestParams as unknown as Parameters<typeof saveChart>[0])
        .then(() => {
          notification.success({
            description: formatMessage(
              { id: 'dbl_multi_save_success' },
              { name: getLanguage(isEn, oParams?.name ?? '', oParams?.nameCn ?? '') }
            ),
          });
          setShowSaveModel(false);
          setSkipNextLeavePrompt(true);
          setTimeout(() => {
            history?.push(`/datalab/${gameCode}/WorkBench/MyAnalysis`);
          }, 0);
        })
        .catch((error: any) => {
          console.warn('保存失败:', error);
          notification.error({
            description: formatMessage(
              { id: 'dbl_multi_save_error' },
              { code: error?.code ?? -1 }
            ),
          });
        });
    },
    [formState, gameCode, taskId, formatMessage, isEn]
  );

  // ---------- 编辑态：进入时拉取任务详情并恢复表单、触发一次计算 ----------
  useEffect(() => {
    if (!isEdit || !taskId) {
      return;
    }
    getChartDetail({ task_id: taskId, game_code: gameCode })
      .then((result: any) => {
        setDetailData(result);
        let filter: any = {};
        try {
          filter =
            typeof result?.filter === 'string' && result?.filter
              ? JSON.parse(result.filter)
              : (result?.filter || {});
        } catch (error) {
          console.warn('任务详情 filter 解析失败，回退为空对象:', error);
          filter = {};
        }
        const { formState: savedFormState } = filter || {};
        const {
          dateRange: savedDateRange,
          ...restSavedFormState
        } = savedFormState || {};
        const newFormState = {
          ...restSavedFormState,
          dateRange: [
            moment(savedDateRange?.[0]),
            moment(savedDateRange?.[1]),
          ],
        };
        setFormState(newFormState);
        onCalculate(newFormState);
      })
      .catch((error: any) => {
        setDetailData({});
        notification.error({
          description: formatMessage(
            { id: 'dbl_ab_experiment_load_task_error' },
            { code: String(error?.code ?? error?.message ?? -1) }
          ),
        });
      });
    // 仅编辑态进入时执行一次；不将 onCalculate 加入依赖，避免 formState 变化触发重复请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, taskId, gameCode]);

  // 表单、配置、表格与操作一并返回，供页面消费
  return {
    formState,
    updateFormState,
    handleUserTypeChange,
    tableData,
    tableLoading,
    hasCalculated,
    hashEqual,
    hashEqualForSave,
    skipNextLeavePrompt,
    isMaxWidth,
    isOverLimit,
    disableCalculateButton,
    showSaveModel,
    setShowSaveModel,
    detailData,
    onCalculate,
    onPreviewSQL,
    onDownload,
    onDownloadConfirm,
    onDownloadModalClose,
    downloadModalVisible,
    downloadRowLimitValue,
    setDownloadRowLimitValue,
    onClearAll,
    saveHandle,
    sqlPreviewLoading,
    allFilters,
    optionFilters,
    eventList,
    allMetrics,
    optionMetrics,
    optionCustomMetrics,
    allDimensions,
    optionDimensions,
    experimentFilters,
    loading,
  };
}
